import { useLocation } from "@tanstack/react-router";
import { ChevronDown, Menu, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useLang, useLangPrefix } from "@/contexts/lang";

function AboutDropdown() {
	const [open, setOpen] = useState(false);
	const ref = useRef<HTMLDivElement>(null);
	const t = useLang();
	const prefix = useLangPrefix();

	const aboutSubmenu = [
		{ label: t.nav.aboutSubmenu.profile, href: `${prefix}/about` },
		{
			label: t.nav.aboutSubmenu.upravenOdbor,
			href: `${prefix}/about/upraven-odbor`,
		},
		{
			label: t.nav.aboutSubmenu.nadzorenOdbor,
			href: `${prefix}/about/nadzoren-odbor`,
		},
		{
			label: t.nav.aboutSubmenu.organizaciskaStruktura,
			href: `${prefix}/about/organizaciska-struktura`,
		},
		{
			label: t.nav.aboutSubmenu.hotelskiMenadzment,
			href: `${prefix}/about/hotelski-menadzment`,
		},
		{
			label: t.nav.aboutSubmenu.povazniAkti,
			href: `${prefix}/about/povazni-akti`,
		},
	];

	useEffect(() => {
		function handler(e: MouseEvent) {
			if (ref.current && !ref.current.contains(e.target as Node)) {
				setOpen(false);
			}
		}
		document.addEventListener("mousedown", handler);
		return () => document.removeEventListener("mousedown", handler);
	}, []);

	return (
		<div ref={ref} className="relative">
			<button
				type="button"
				onClick={() => setOpen((v) => !v)}
				className="group relative flex items-center gap-1 text-base font-semibold tracking-wide text-primary/80 transition-colors duration-500 hover:text-primary cursor-pointer"
			>
				{t.nav.about}
				<ChevronDown
					className={`h-3.5 w-3.5 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
				/>
				<span className="absolute -bottom-1 left-0 h-px w-0 bg-accent transition-all duration-500 group-hover:w-full" />
			</button>

			{open ? (
				<div className="absolute left-0 top-full z-50 mt-2 w-64 rounded-sm border border-primary/[0.06] bg-cream shadow-luxury py-2">
					<a
						href={aboutSubmenu[0].href}
						onClick={() => setOpen(false)}
						className="block px-5 py-2.5 text-sm font-semibold text-primary/80 transition-colors duration-200 hover:bg-secondary hover:text-primary"
					>
						{aboutSubmenu[0].label}
					</a>
					<div className="mx-5 my-1.5 border-t border-primary/5" />
					<p className="px-5 pt-1 pb-1.5 text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">
						{t.nav.aboutSubmenu.organizacija}
					</p>
					{aboutSubmenu.slice(1).map((item) => (
						<a
							key={item.href}
							href={item.href}
							onClick={() => setOpen(false)}
							className="block px-5 py-2.5 text-sm font-semibold text-primary/80 transition-colors duration-200 hover:bg-secondary hover:text-primary"
						>
							{item.label}
						</a>
					))}
				</div>
			) : null}
		</div>
	);
}

export default function Header() {
	const [isOpen, setIsOpen] = useState(false);
	const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
	const t = useLang();
	const prefix = useLangPrefix();
	const { pathname } = useLocation();
	const isEn = prefix === "/en";

	const mkHref = isEn ? pathname.slice(3) || "/" : pathname;
	const enHref = isEn ? pathname : `/en${pathname}`;

	const aboutSubmenuMobile = [
		{ label: t.nav.aboutSubmenu.profile, href: `${prefix}/about` },
		{
			label: t.nav.aboutSubmenu.upravenOdbor,
			href: `${prefix}/about/upraven-odbor`,
		},
		{
			label: t.nav.aboutSubmenu.nadzorenOdbor,
			href: `${prefix}/about/nadzoren-odbor`,
		},
		{
			label: t.nav.aboutSubmenu.organizaciskaStruktura,
			href: `${prefix}/about/organizaciska-struktura`,
		},
		{
			label: t.nav.aboutSubmenu.hotelskiMenadzment,
			href: `${prefix}/about/hotelski-menadzment`,
		},
		{
			label: t.nav.aboutSubmenu.povazniAkti,
			href: `${prefix}/about/povazni-akti`,
		},
	];

	useEffect(() => {
		function onKeyDown(e: KeyboardEvent) {
			if (e.key === "Escape") {
				setIsOpen(false);
				setMobileAboutOpen(false);
			}
		}

		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, []);

	return (
		<header className="sticky top-0 z-50">
			<div className="border-b border-primary/[0.04] bg-cream/90 backdrop-blur-xl supports-[backdrop-filter]:bg-cream/80">
				<div className="mx-auto flex max-w-7xl items-center justify-between gap-8 px-4 sm:px-6 lg:px-8 py-5 sm:py-6">
					{/* Logo */}
					<a
						href={prefix || "/"}
						className="group inline-flex items-center gap-3.5 transition-opacity duration-500 hover:opacity-80"
					>
						<img
							src="/logo.svg"
							alt="Македонијатурист"
							className="h-12 sm:h-14 w-auto"
						/>
						<div>
							<span className="font-display text-base sm:text-lg font-medium tracking-wide text-primary block leading-tight">
								{isEn ? "MAKEDONIJATURIST A.D." : "МАКЕДОНИЈАТУРИСТ А.Д."}
							</span>
							<span className="text-[10px] sm:text-[11px] font-medium tracking-[0.18em] text-muted-foreground uppercase">
								{isEn ? "Hotel Group" : "Хотелска групација"}
							</span>
						</div>
					</a>

					{/* Desktop Navigation */}
					<nav className="hidden items-center gap-10 lg:flex">
						<AboutDropdown />

						<a
							href={`${prefix}/#portfolio`}
							className="group relative text-base font-semibold tracking-wide text-primary/80 transition-colors duration-500 hover:text-primary"
						>
							{t.nav.ourHotels}
							<span className="absolute -bottom-1 left-0 h-px w-0 bg-accent transition-all duration-500 group-hover:w-full" />
						</a>

						<a
							href={`${prefix}/news`}
							className="group relative text-base font-semibold tracking-wide text-primary/80 transition-colors duration-500 hover:text-primary"
						>
							{t.nav.news}
							<span className="absolute -bottom-1 left-0 h-px w-0 bg-accent transition-all duration-500 group-hover:w-full" />
						</a>

						<a
							href={`${prefix}/investitori`}
							className="group relative text-base font-semibold tracking-wide text-primary/80 transition-colors duration-500 hover:text-primary"
						>
							{t.nav.investors}
							<span className="absolute -bottom-1 left-0 h-px w-0 bg-accent transition-all duration-500 group-hover:w-full" />
						</a>

						<a
							href={`${prefix}/contact`}
							className="group relative text-base font-semibold tracking-wide text-primary/80 transition-colors duration-500 hover:text-primary"
						>
							{t.nav.contact}
							<span className="absolute -bottom-1 left-0 h-px w-0 bg-accent transition-all duration-500 group-hover:w-full" />
						</a>

						<a
							href={`${prefix}/career`}
							className="inline-flex items-center gap-1.5 rounded-sm border border-accent bg-accent/10 px-5 py-2 text-base font-semibold tracking-wide text-primary transition-all duration-300 hover:bg-accent hover:text-white"
						>
							{t.nav.career}
						</a>

						{/* Language selector */}
						<div className="flex items-center border-l border-primary/10 pl-8 ml-2">
							<a
								href={mkHref}
								className={`text-xs font-semibold tracking-wide transition-colors duration-300 cursor-pointer ${!isEn ? "text-primary" : "text-primary/40 hover:text-primary"}`}
							>
								MK
							</a>
							<span className="mx-2 text-primary/20">/</span>
							<a
								href={enHref}
								className={`text-xs font-semibold tracking-wide transition-colors duration-300 cursor-pointer ${isEn ? "text-primary" : "text-primary/40 hover:text-primary"}`}
							>
								EN
							</a>
						</div>
					</nav>

					{/* Mobile menu button */}
					<button
						type="button"
						className="inline-flex items-center justify-center rounded-sm border border-primary/[0.06] bg-white p-2.5 text-primary transition-all duration-300 hover:bg-secondary hover:border-primary/10 lg:hidden shadow-luxury-sm cursor-pointer"
						aria-label={isOpen ? t.nav.mobileClose : t.nav.mobileOpen}
						onClick={() => setIsOpen((v) => !v)}
					>
						{isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
					</button>
				</div>
			</div>

			{/* Mobile menu */}
			{isOpen ? (
				<div className="border-b border-primary/[0.04] bg-cream lg:hidden">
					<nav className="mx-auto max-w-7xl px-4 sm:px-6 py-5">
						<div className="grid gap-1">
							{/* About accordion */}
							<div>
								<button
									type="button"
									onClick={() => setMobileAboutOpen((v) => !v)}
									className="flex w-full items-center justify-between rounded-sm px-4 py-3 text-sm font-semibold text-primary/80 transition-colors duration-300 hover:bg-secondary hover:text-primary cursor-pointer"
								>
									{t.nav.about}
									<ChevronDown
										className={`h-4 w-4 transition-transform duration-300 ${mobileAboutOpen ? "rotate-180" : ""}`}
									/>
								</button>
								{mobileAboutOpen ? (
									<div className="ml-4 mt-0.5 grid gap-0.5 border-l border-primary/10 pl-4">
										{aboutSubmenuMobile.map((item) => (
											<a
												key={item.href}
												href={item.href}
												className="rounded-sm px-3 py-2.5 text-sm font-semibold text-primary/70 transition-colors duration-300 hover:bg-secondary hover:text-primary"
												onClick={() => {
													setIsOpen(false);
													setMobileAboutOpen(false);
												}}
											>
												{item.label}
											</a>
										))}
									</div>
								) : null}
							</div>

							<a
								href={`${prefix}/#portfolio`}
								className="rounded-sm px-4 py-3 text-sm font-semibold text-primary/80 transition-colors duration-300 hover:bg-secondary hover:text-primary"
								onClick={() => setIsOpen(false)}
							>
								{t.nav.ourHotels}
							</a>

							<a
								href={`${prefix}/news`}
								className="rounded-sm px-4 py-3 text-sm font-semibold text-primary/80 transition-colors duration-300 hover:bg-secondary hover:text-primary"
								onClick={() => setIsOpen(false)}
							>
								{t.nav.news}
							</a>

							<a
								href={`${prefix}/investitori`}
								className="rounded-sm px-4 py-3 text-sm font-semibold text-primary/80 transition-colors duration-300 hover:bg-secondary hover:text-primary"
								onClick={() => setIsOpen(false)}
							>
								{t.nav.investors}
							</a>

							<a
								href={`${prefix}/contact`}
								className="rounded-sm px-4 py-3 text-sm font-semibold text-primary/80 transition-colors duration-300 hover:bg-secondary hover:text-primary"
								onClick={() => setIsOpen(false)}
							>
								{t.nav.contact}
							</a>

							<a
								href={`${prefix}/career`}
								onClick={() => setIsOpen(false)}
								className="mt-1 inline-flex items-center gap-1.5 rounded-sm border border-accent bg-accent/10 px-4 py-2.5 text-sm font-semibold text-primary transition-all duration-300 hover:bg-accent hover:text-white"
							>
								{t.nav.career}
							</a>

							{/* Language selector */}
							<div className="flex items-center gap-2 px-4 pt-4 mt-3 border-t border-primary/5">
								<a
									href={mkHref}
									className={`text-xs font-semibold tracking-wide cursor-pointer ${!isEn ? "text-primary" : "text-primary/40 hover:text-primary transition-colors duration-300"}`}
								>
									MK
								</a>
								<span className="text-primary/20">/</span>
								<a
									href={enHref}
									className={`text-xs font-semibold tracking-wide cursor-pointer ${isEn ? "text-primary" : "text-primary/40 hover:text-primary transition-colors duration-300"}`}
								>
									EN
								</a>
							</div>
						</div>
					</nav>
				</div>
			) : null}
		</header>
	);
}
